// src/components/settings/sections/PermissionsSettings.jsx

import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Shield, Mail, User } from 'lucide-react';
import { collection, addDoc, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../../config/firebase';

export const PermissionsSettings = ({ 
  bookId, 
  bookData, 
  templateType,
  currentUser 
}) => {
  const [organizers, setOrganizers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('organizer');
  const [loading, setLoading] = useState(false);

  // Role options based on template
  const getRoleOptions = () => {
    switch (templateType) {
      case 'events':
        return [
          { value: 'organizer', label: 'Organizer', description: 'Can manage registrations and schedule' },
          { value: 'admin', label: 'Admin', description: 'Full access except deleting the event' }
        ];
      case 'institution':
        return [
          { value: 'faculty', label: 'Faculty', description: 'Can manage programs and courses' },
          { value: 'coordinator', label: 'Coordinator', description: 'Can manage admissions' },
          { value: 'admin', label: 'Admin', description: 'Full access' }
        ];
      case 'organisation':
        return [
          { value: 'editor', label: 'Editor', description: 'Can edit content' },
          { value: 'manager', label: 'Manager', description: 'Can manage team' },
          { value: 'admin', label: 'Admin', description: 'Full access' }
        ];
      default:
        return [
          { value: 'editor', label: 'Editor', description: 'Can edit content' },
          { value: 'admin', label: 'Admin', description: 'Full access' }
        ];
    }
  };

  const roleOptions = getRoleOptions();

  useEffect(() => {
    loadOrganizers();
  }, [bookId]);

  const loadOrganizers = async () => {
    try {
      const collectionName = getCollectionName(templateType);
      const organizersRef = collection(db, `pb-books/${bookId}/${collectionName}`);
      const snapshot = await getDocs(organizersRef);
      
      const organizersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setOrganizers(organizersData);
    } catch (error) {
      console.error('Error loading organizers:', error);
    }
  };

  const getCollectionName = (templateType) => {
    switch (templateType) {
      case 'events': return 'pb-organizers';
      case 'institution': return 'pb-staff';
      case 'organisation': return 'pb-team';
      default: return 'pb-members';
    }
  };

  const handleAddOrganizer = async (e) => {
    e.preventDefault();
    if (!newEmail) return;

    setLoading(true);
    try {
      const collectionName = getCollectionName(templateType);
      const organizersRef = collection(db, `pb-books/${bookId}/${collectionName}`);
      
      await addDoc(organizersRef, {
        email: newEmail,
        role: newRole,
        addedAt: new Date(),
        addedBy: currentUser.uid
      });

      setNewEmail('');
      setShowAddForm(false);
      await loadOrganizers();
    } catch (error) {
      console.error('Error adding organizer:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveOrganizer = async (organizerId) => {
    if (!window.confirm('Are you sure you want to remove this person\'s access?')) return;

    try {
      const collectionName = getCollectionName(templateType);
      await deleteDoc(doc(db, `pb-books/${bookId}/${collectionName}`, organizerId));
      await loadOrganizers();
    } catch (error) {
      console.error('Error removing organizer:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Access Management
        </h3>
        <p className="text-sm text-gray-600">
          Manage who can access the management portal
        </p>
      </div>

      {/* Owner Display */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Shield className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-blue-900">Owner</h4>
            <p className="text-sm text-blue-700">
              {bookData.ownerId || 'Book Owner'} • Full Access
            </p>
          </div>
        </div>
      </div>

      {/* Organizers List */}
      <div className="space-y-3">
        {organizers.map(organizer => (
          <div 
            key={organizer.id}
            className="bg-white border border-gray-200 rounded-lg p-4"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <User className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">
                    {organizer.name || organizer.email}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {organizer.email}
                  </p>
                  <span className="inline-block mt-1 px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full capitalize">
                    {organizer.role}
                  </span>
                </div>
              </div>
              
              <button
                onClick={() => handleRemoveOrganizer(organizer.id)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}

        {organizers.length === 0 && (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <Shield className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No additional access granted</p>
          </div>
        )}
      </div>

      {/* Add Organizer Form */}
      {showAddForm ? (
        <form onSubmit={handleAddOrganizer} className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              required
              placeholder="colleague@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {roleOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label} - {option.description}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Adding...' : 'Add Access'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setNewEmail('');
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="h-5 w-5" />
          Grant Access
        </button>
      )}
    </div>
  );
};